import { useEffect, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import {
  getCustomerMeta, upsertCustomerMeta, listMMUpdates, createMMUpdate, deleteMMUpdate,
  listAudits, createAudit, getAuditExercises,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RosterSelect } from "@/components/RosterAutocomplete";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";
import {
  MessagesSquare, Target, Dumbbell, AlertTriangle, Plus, Trash2, CalendarDays,
} from "lucide-react";

const REACHOUT_TYPES = ["Call", "WhatsApp", "In-person", "Email"];

// A biweekly reach-out is "stale" if the latest one is more than 14 days ago (or none exist).
function isStale(latestDate) {
  if (!latestDate) return true;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(latestDate); d.setHours(0, 0, 0, 0);
  return (today - d) / (1000 * 60 * 60 * 24) > 14;
}

function daysSince(d) {
  if (!d) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const dt = new Date(d); dt.setHours(0, 0, 0, 0);
  return Math.round((t - dt) / (1000 * 60 * 60 * 24));
}

/**
 * Member Manager panel — everything for a selected customer:
 *   • Goal (persists in customer_meta)
 *   • Biweekly reach-outs from the Member Manager
 *   • The Audit (bi-monthly strength baseline snapshots)
 *   • Non-renewal reason (only shown for expired customers)
 *
 * Props:
 *   - customer: the aggregated customer object from GET /api/customers
 *   - lifecycle: "active" | "expired" | "unknown"
 *   - isFE: boolean — true if this customer has an active accountability tracker (FE programme)
 */
export default function MemberManagerPanel({ customer, lifecycle, isFE }) {
  const askDelete = useDeleteAsk();
  const key = customer.key;
  const [meta, setMeta] = useState({});
  const [updates, setUpdates] = useState([]);
  const [audits, setAudits] = useState([]);
  const [reason, setReason] = useState("");
  const [goal, setGoal] = useState("");

  const load = () => {
    getCustomerMeta(key).then((m) => {
      setMeta(m || {});
      setGoal(m?.goal || "");
      setReason(m?.non_renewal_reason || "");
    }).catch(() => setMeta({}));
    listMMUpdates(key).then(setUpdates).catch(() => setUpdates([]));
    listAudits(key).then(setAudits).catch(() => setAudits([]));
  };
  useEffect(() => { load(); }, [key]);

  const saveGoal = async () => {
    await upsertCustomerMeta(key, { goal, customer_name: customer.name });
    toast.success("Goal saved");
    load();
  };
  const saveReason = async () => {
    if (!reason.trim()) return toast.error("Please enter a reason");
    await upsertCustomerMeta(key, { non_renewal_reason: reason, customer_name: customer.name });
    toast.success("Non-renewal reason recorded");
    load();
  };

  const latestUpdate = updates[0]?.date;
  const stale = !isFE && lifecycle === "active" && isStale(latestUpdate);
  const needsReason = lifecycle === "expired" && !(meta?.non_renewal_reason || "").trim();

  return (
    <div className="space-y-6" data-testid="member-manager-panel">
      {/* Flags for this customer */}
      {(stale || needsReason || (isFE && lifecycle === "active")) && (
        <div className="space-y-2">
          {isFE && lifecycle === "active" && (
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/[0.05] px-4 py-2.5 text-xs text-blue-200 flex items-start gap-2" data-testid="fe-badge">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5" />
              <span>This member is currently in the <span className="text-zinc-100 font-medium">Front End programme</span> — accountability updates live on the <span className="text-zinc-100 font-medium">Accountability</span> page.</span>
            </div>
          )}
          {stale && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] px-4 py-2.5 text-xs text-amber-200 flex items-start gap-2" data-testid="stale-update-flag">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5" />
              <span>No Member Manager update in the last 14 days{latestUpdate ? ` — last update was ${daysSince(latestUpdate)} days ago` : " — no updates on file yet"}.</span>
            </div>
          )}
          {needsReason && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/[0.05] px-4 py-2.5 text-xs text-rose-200 flex items-start gap-2" data-testid="needs-reason-flag">
              <AlertTriangle className="h-3.5 w-3.5 text-rose-400 mt-0.5" />
              <span>This member&apos;s programme has ended without renewing — please record why below.</span>
            </div>
          )}
        </div>
      )}

      {/* Goal */}
      <section className="border border-[#1f1f1f] rounded-xl p-4 bg-[#0d0d0d]" data-testid="member-goal-section">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-3.5 w-3.5 text-emerald-400" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Member goal</div>
        </div>
        <Textarea
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="What is this member working toward? (e.g. Get to sub-15% body fat by Diwali)"
          className="bg-[#0a0a0a] border-[#262626]"
          rows={2}
          data-testid="member-goal-input"
        />
        <div className="mt-2 flex items-center justify-between">
          <div className="text-[10px] text-zinc-500">{meta?.updated_at ? `Updated ${new Date(meta.updated_at).toLocaleDateString()}` : "Not set yet"}</div>
          <Button onClick={saveGoal} size="sm" className="bg-emerald-600 hover:bg-emerald-500 rounded-full" data-testid="member-goal-save-btn">Save goal</Button>
        </div>
      </section>

      {/* Non-renewal reason — expired only */}
      {lifecycle === "expired" && (
        <section className={`border rounded-xl p-4 bg-[#0d0d0d] ${needsReason ? "border-rose-500/30" : "border-[#1f1f1f]"}`} data-testid="non-renewal-section">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className={`h-3.5 w-3.5 ${needsReason ? "text-rose-400" : "text-zinc-500"}`} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Non-renewal reason</div>
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why didn't this member renew? (e.g. Relocating to Bangalore / Cost concern / Health break)"
            className="bg-[#0a0a0a] border-[#262626]"
            rows={2}
            data-testid="non-renewal-input"
          />
          <div className="mt-2 flex items-center justify-between">
            <div className="text-[10px] text-zinc-500">
              {meta?.non_renewal_recorded_at && (meta?.non_renewal_reason || "").trim()
                ? `Recorded ${new Date(meta.non_renewal_recorded_at).toLocaleDateString()}`
                : "Required — programme has ended without a renewal"}
            </div>
            <Button onClick={saveReason} size="sm" className="bg-rose-600 hover:bg-rose-500 rounded-full" data-testid="non-renewal-save-btn">Save reason</Button>
          </div>
        </section>
      )}

      {/* Member Manager updates */}
      {!isFE && (
        <section className="border border-[#1f1f1f] rounded-xl p-4 bg-[#0d0d0d]" data-testid="mm-updates-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MessagesSquare className="h-3.5 w-3.5 text-blue-400" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Member Manager updates · biweekly</div>
            </div>
          </div>
          <AddUpdateForm customerKey={key} customerName={customer.name} onCreated={load} />
          {updates.length === 0 ? (
            <div className="text-xs text-zinc-500 text-center py-6">No updates yet — record the first reach-out above.</div>
          ) : (
            <ul className="mt-3 space-y-2">
              {updates.map((u) => (
                <li key={u.id} className="rounded-lg border border-[#1a1a1a] bg-[#0a0a0a] px-3 py-2 flex items-start gap-3" data-testid={`mm-update-${u.id}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-zinc-200">{u.note}</div>
                      <button
                        onClick={() => { const reason = askDelete("this update"); if (reason === null) return;
                          deleteMMUpdate(u.id, reason).then((r) => { if (!deletionRequested(r)) load(); }).catch((e) => toast.error(e?.response?.data?.detail || "Delete failed")); }}
                        className="text-zinc-500 hover:text-rose-400 p-1"
                        aria-label="Delete update"
                        data-testid={`delete-mm-update-${u.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1"><CalendarDays className="h-2.5 w-2.5" />{new Date(u.date).toLocaleDateString()}</span>
                      {u.source === "no_show" && <span className="px-1.5 py-0.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300" data-testid={`mm-update-noshow-${u.id}`}>No-show follow-up</span>}
                      {u.reachout_type && <span className="px-1.5 py-0.5 rounded border border-[#262626] bg-[#0d0d0d] text-zinc-400">{u.reachout_type}</span>}
                      {(u.logged_by || u.member_manager) && <span className="text-zinc-400">by {u.logged_by || u.member_manager}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* The Audit — bi-monthly */}
      <section className="border border-[#1f1f1f] rounded-xl p-4 bg-[#0d0d0d]" data-testid="audit-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Dumbbell className="h-3.5 w-3.5 text-orange-400" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">The Audit · bi-monthly benchmarks</div>
          </div>
        </div>
        <AddAuditForm customerKey={key} customerName={customer.name} onCreated={load} />
        <AuditHistory audits={audits} />
      </section>
    </div>
  );
}

function AddUpdateForm({ customerKey, customerName, onCreated }) {
  const [note, setNote] = useState("");
  const [reachoutType, setReachoutType] = useState("Call");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [manager, setManager] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return toast.error("Note is required");
    setBusy(true);
    try {
      await createMMUpdate({
        customer_key: customerKey, customer_name: customerName,
        note, reachout_type: reachoutType, date, member_manager: manager || null,
      });
      toast.success("Update logged");
      setNote("");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <form data-testid="member-manager-panel-form-234" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
      <div className="md:col-span-3">
        <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Note *</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What did you discuss?" className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="mm-update-note" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Reach-out</Label>
        <Select data-testid="member-manager-panel-select-241" value={reachoutType} onValueChange={setReachoutType}>
          <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="mm-update-type"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
            {REACHOUT_TYPES.map((v) => (<SelectItem data-testid="member-manager-panel-select-item-244" key={v} value={v}>{v}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Date</Label>
        <DatePicker value={date} onChange={(e) => setDate(e.target.value)} data-testid="mm-update-date" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Manager</Label>
        <RosterSelect roleType="member_manager" value={manager} onChange={setManager} testid="mm-update-manager" />
      </div>
      <Button type="submit" disabled={busy} size="sm" className="bg-blue-600 hover:bg-blue-500 rounded-full h-9" data-testid="mm-update-submit">
        <Plus className="h-3 w-3 mr-1" /> Log
      </Button>
    </form>
  );
}

function AuditHistory({ audits }) {
  const [exercises, setExercises] = useState([]);
  useEffect(() => { getAuditExercises().then((r) => setExercises((r.items || []).filter((x) => x.active !== false))).catch(() => setExercises([])); }, []);
  if (audits.length === 0) {
    return <div className="text-xs text-zinc-500 text-center py-6 mt-3">No audits recorded yet.</div>;
  }
  const legacyMap = {
    "Deadlift": "deadlift_1rm", "Back Squat": "back_squat_1rm",
    "Overhead Press": "overhead_press_1rm", "OHP": "overhead_press_1rm",
    "VO2 max": "vo2_max", "Body Fat %": "body_fat_pct",
    "Max Pushups / min": "max_pushups_per_min", "Max Pullups / min": "max_pullups_per_min",
  };
  const readVal = (a, ex) => {
    const m = a.metrics || {};
    if (m[ex.name] != null && m[ex.name] !== "") return m[ex.name];
    const legacyKey = legacyMap[ex.name];
    if (legacyKey && a[legacyKey] != null) return a[legacyKey];
    return null;
  };
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
            <th className="px-2 py-2 font-medium">Date</th>
            {exercises.map((ex) => (
              <th key={ex.name} className="px-2 py-2 font-medium text-right whitespace-nowrap">{ex.name}{ex.unit ? <span className="text-zinc-600 font-normal"> ({ex.unit})</span> : null}</th>
            ))}
            <th className="px-2 py-2 font-medium">6-month goal</th>
          </tr>
        </thead>
        <tbody>
          {(audits || []).map((a) => (
            <tr key={a.id} className="border-b border-[#1a1a1a] last:border-0" data-testid={`audit-row-${a.id}`}>
              <td className="px-2 py-2 text-zinc-400 whitespace-nowrap">{new Date(a.audit_date).toLocaleDateString()}</td>
              {exercises.map((ex) => (
                <td key={ex.name} className="px-2 py-2 text-right font-mono text-zinc-200">{readVal(a, ex) ?? "—"}</td>
              ))}
              <td className="px-2 py-2 text-zinc-400 max-w-[16rem] truncate" title={a.six_month_goal}>{a.six_month_goal || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddAuditForm({ customerKey, customerName, onCreated }) {
  const [open, setOpen] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [f, setF] = useState({
    audit_date: new Date().toISOString().slice(0, 10),
    six_month_goal: "", notes: "", member_manager: "",
  });
  const [metrics, setMetrics] = useState({}); // { exerciseName: string }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getAuditExercises().then((r) => setExercises((r.items || []).filter((x) => x.active !== false))).catch(() => setExercises([]));
  }, []);

  const setMetric = (name, val) => setMetrics((m) => ({ ...m, [name]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Preserve backwards-compat legacy columns when the user filled Deadlift/Back Squat/OHP/VO2/BF/pushups/pullups.
      const legacyPayload = {};
      const parse = (v) => (v === "" || v == null ? null : Number(v));
      const mapping = {
        "Deadlift": "deadlift_1rm", "Back Squat": "back_squat_1rm",
        "Overhead Press": "overhead_press_1rm", "OHP": "overhead_press_1rm",
        "VO2 max": "vo2_max", "Body Fat %": "body_fat_pct",
        "Max Pushups / min": "max_pushups_per_min", "Max Pullups / min": "max_pullups_per_min",
      };
      for (const [k, dbKey] of Object.entries(mapping)) {
        if (metrics[k] !== undefined && metrics[k] !== "") legacyPayload[dbKey] = parse(metrics[k]);
      }
      // metrics dict — keep every entered value as string OR number so units like "mm:ss" survive.
      const cleanMetrics = {};
      for (const [k, v] of Object.entries(metrics)) {
        if (v === "" || v == null) continue;
        const asNum = Number(v);
        cleanMetrics[k] = Number.isFinite(asNum) && String(v).match(/^-?\d+(\.\d+)?$/) ? asNum : v;
      }
      await createAudit({
        customer_key: customerKey, customer_name: customerName,
        audit_date: f.audit_date,
        metrics: cleanMetrics,
        ...legacyPayload,
        six_month_goal: f.six_month_goal || null,
        notes: f.notes || null,
        member_manager: f.member_manager || null,
      });
      toast.success("Audit recorded");
      setOpen(false);
      setMetrics({});
      setF((s) => ({ ...s, six_month_goal: "", notes: "" }));
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <Button size="sm" onClick={() => setOpen(true)} className="bg-orange-600 hover:bg-orange-500 rounded-full" data-testid="add-audit-btn">
        <Plus className="h-3 w-3 mr-1" /> Add audit
      </Button>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-orange-500/20 bg-orange-500/[0.02] p-3 mb-3" data-testid="add-audit-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Sub label="Audit date"><DatePicker value={f.audit_date} onChange={(e) => setF({ ...f, audit_date: e.target.value })} data-testid="audit-date" /></Sub>
        <Sub label="Manager"><RosterSelect roleType="member_manager" value={f.member_manager} onChange={(v) => setF({ ...f, member_manager: v })} testid="audit-manager" /></Sub>
        {exercises.map((ex) => (
          <Sub key={ex.name} label={`${ex.name}${ex.unit ? ` (${ex.unit})` : ""}`}>
            <Input
              value={metrics[ex.name] ?? ""}
              onChange={(e) => setMetric(ex.name, e.target.value)}
              placeholder={ex.unit || "—"}
              className="bg-[#0a0a0a] border-[#262626] font-mono h-9"
              data-testid={`audit-metric-${ex.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            />
          </Sub>
        ))}
      </div>
      <Sub label="6-month goal"><Input value={f.six_month_goal} onChange={(e) => setF({ ...f, six_month_goal: e.target.value })} placeholder="e.g. Deadlift 140kg by August" className="bg-[#0a0a0a] border-[#262626]" data-testid="audit-goal" /></Sub>
      <div className="flex justify-end gap-2">
        <Button data-testid="member-manager-panel-button-394" type="button" variant="outline" size="sm" onClick={() => setOpen(false)} className="border-[#262626] bg-transparent hover:bg-[#141414]">Cancel</Button>
        <Button type="submit" disabled={busy} size="sm" className="bg-orange-600 hover:bg-orange-500 rounded-full" data-testid="audit-submit">
          {busy ? "Saving…" : "Save audit"}
        </Button>
      </div>
    </form>
  );
}

function Sub({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
